import { Component, OnInit, Signal, computed, inject, DestroyRef, effect } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ProducersService } from '../producers.service';
import { CarouselComponent } from '../../../shared/carousel/carousel.component';
import { capitalizeWords, handleImageError } from '../../../shared/common/utils';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-producer-detail',
    imports: [RouterLink, CommonModule, CarouselComponent],
    templateUrl: './producer-detail.component.html',
    styleUrl: './producer-detail.component.scss'
})
export class ProducerDetailComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private producersService = inject(ProducersService);
  private meta = inject(Meta);
  private title = inject(Title);
  private sanitizer = inject(DomSanitizer);
  #router = inject(Router);

  /** robots as it was before this page marked itself not found; undefined while unmarked. */
  #robotsBeforeNotFound: string | null | undefined;

  foodItem: any[] = []
  seoURL!: string;
  producerDetails: Signal<any> = computed(
    () => this.producersService.producerDetailsSignal() || null
  );
  loadingProducts: Signal<any> = computed(
    () => !!this.producersService.isLoadingProducts()
  );
  carouselProducerProducts: Signal<any | undefined> = computed(() =>
    this.setUpCarouselItems()
  );

  handleImageError = handleImageError;
  producerNotFound = computed(() => this.producersService.producerError());

  constructor() {
    // server.ts answers these with a 410; the served HTML must not point Google
    // at the URL as a page (DEV-11795). This app has no canonical, so only the
    // og:url/twitter:url and robots are touched. The component is reused when
    // navigating between producers, hence the else.
    effect(() => {
      if (this.producerNotFound()) this.#markNotFound();
      else this.#restoreRobots();
    });
    inject(DestroyRef).onDestroy(() => this.#restoreRobots());
  }

  ngOnInit(): void {
    this.handleRouteChanges();
  }

  private setUpSEOTags(producer: string) {
    const { baseTitle, baseURL, type } = environment.config.seo;
    const url = `pages/producers/${producer}`
    const title = `${capitalizeWords(producer)} | ${baseTitle}`;

    // Page title
    this.title.setTitle(title);

    // Open Graph for social networks:
    this.meta.addTag({ property: 'og:title', content: title });
    this.meta.addTag({ property: 'og:url', content: `${baseURL}${url}` });
    this.meta.addTag({ property: 'og:type', content: type });
  }

  private handleRouteChanges(): any {
    this.activatedRoute.params.subscribe((params) => {
      this.seoURL = `${params['producerUrl']}`;
      this.producersService.getProducerByUrl(this.seoURL);
      this.setUpSEOTags(this.seoURL);
    });
  }

  private setUpCarouselItems() {
    const items = this.producerDetails()?.products.filter((p: any) => p !== false).map((product: any) => {
      product.image = product.img;
      product.name = product.name;
      if (typeof product?.price === 'number')
        product.price = product.price.toFixed(2);

      return {
        content: {
          product: product,
          settings: { isCardInOrderPage: true },
        },
      };
    });
    return items;
  }

  sanitizeHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  back() {
    this.#router.navigate(['/pages/producers']);
  }

  goToHome() {
    this.#router.navigate(['/home']);
  }

  #markNotFound() {
    if (this.#robotsBeforeNotFound === undefined) {
      this.#robotsBeforeNotFound = this.meta.getTag('name="robots"')?.content ?? null;
    }
    // setUpSEOTags adds og:url with addTag, so there can be more than one.
    for (const selector of ['property="og:url"', 'name="twitter:url"']) {
      this.meta.getTags(selector).forEach(tag => this.meta.removeTagElement(tag));
    }
    this.meta.updateTag({ name: 'robots', content: 'noindex' });
  }

  /** Puts robots back as index.html shipped it, so the noindex doesn't follow the visitor. */
  #restoreRobots() {
    if (this.#robotsBeforeNotFound === undefined) return;
    if (this.#robotsBeforeNotFound === null) this.meta.removeTag('name="robots"');
    else this.meta.updateTag({ name: 'robots', content: this.#robotsBeforeNotFound });
    this.#robotsBeforeNotFound = undefined;
  }
}
